/** @jsxFrag React.Fragment */
import * as React from 'react';
import { useEffect } from 'react';

import { isEqual } from 'lodash';

import {
  getPhaseOneOverride,
  useEnterpriseAdminPermissions,
} from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import type { RequiredPermission } from 'bundles/enterprise-admin-roles/types';

type Props = {
  requiredPermission: RequiredPermission;
};

// We need to call the hook within a functional component
const RequiredPermissionWrapper = ({ requiredPermission, children }: React.PropsWithChildren<Props>) => {
  const { setCurrentRequiredPermission, currentRequiredPermission } = useEnterpriseAdminPermissions();

  useEffect(() => {
    if (!isEqual(currentRequiredPermission, requiredPermission)) {
      setCurrentRequiredPermission(requiredPermission);
    }
  }, [requiredPermission, setCurrentRequiredPermission, currentRequiredPermission]);

  return <>{children}</>;
};

type SetRequiredPermissionsRequest = {
  requiredPermission: RequiredPermission;
  phaseOneOverridePermission?: boolean;
  waitToCheckPermissions?: boolean;
};

const withRequiredEnterprisePermission =
  <TOuter extends object>(options: (props: TOuter) => SetRequiredPermissionsRequest) =>
  (Component: React.ComponentType<TOuter>) =>
  ({ ...props }: TOuter) => {
    const { requiredPermission, phaseOneOverridePermission, waitToCheckPermissions } = options(props);
    const implicitPhaseOneOverridePermission = getPhaseOneOverride(
      requiredPermission.domain,
      requiredPermission.domainId
    );

    return phaseOneOverridePermission || implicitPhaseOneOverridePermission || waitToCheckPermissions ? (
      <Component {...props} />
    ) : (
      <RequiredPermissionWrapper requiredPermission={requiredPermission}>
        <Component {...props} />
      </RequiredPermissionWrapper>
    );
  };

export default withRequiredEnterprisePermission;
